// The Auftragnehmer's own Stammdaten (spec §1.10, §1.12). In src/shared so the future client validates
// with the same object; every message is the German sentence the form shows.
import { z } from 'zod'
import { addressSchema } from './tenant'

export const VAT_MODES = ['kleinunternehmer', 'regelbesteuert', 'reverse_charge'] as const
/** The single source for the three modes: the DDL's CHECK, the engine and the services all mean this list. */
export type VatMode = (typeof VAT_MODES)[number]

const text = (max: number) =>
  z
    .string()
    .trim()
    .min(1, 'Das Feld darf nicht leer sein; zum Löschen bitte null senden.')
    .max(max, `Höchstens ${max} Zeichen.`)
    .nullable()

const IBAN_RE = /^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/

/**
 * ISO 13616 check: move the first four characters to the end, map A–Z to 10–35, the remainder mod 97 is 1.
 * A shape check alone would accept a transposed digit, and the IBAN is printed on every Rechnung and paid
 * out against — a wrong one has to be refused here, not discovered when the money is gone.
 */
function ibanChecksumOk(iban: string): boolean {
  const rearranged = iban.slice(4) + iban.slice(0, 4)
  let remainder = 0
  for (const char of rearranged) {
    const code = char.charCodeAt(0)
    const chunk = code >= 65 && code <= 90 ? String(code - 55) : char
    for (const digit of chunk) remainder = (remainder * 10 + Number(digit)) % 97
  }
  return remainder === 1
}

/**
 * A bank statement prints the IBAN in groups of four; the form must accept exactly what the owner copies
 * from it and store the unspaced canonical form, so the message and the accepted input cannot disagree.
 */
const ibanSchema = z
  .string()
  .trim()
  .toUpperCase()
  .transform((value) => value.replace(/\s+/g, ''))
  .pipe(
    z
      .string()
      .regex(IBAN_RE, 'IBAN im Format DE02 1203 0000 0000 2020 51 angeben.')
      .refine(ibanChecksumOk, 'Die Prüfziffer der IBAN stimmt nicht — bitte die Nummer prüfen.'),
  )
  .nullable()

export const profilPatchSchema = z
  .object({
    legal_name: text(200),
    legal_form: text(120),
    representative_name: text(200),
    address: addressSchema.nullable(),
    country: z.string().trim().regex(/^[A-Z]{2}$/, 'Land als zweistelliges Kürzel, z. B. DE.'),
    vat_mode: z.enum(VAT_MODES),
    vat_id: text(32),
    tax_number: text(32),
    iban: ibanSchema,
    bic: z.string().trim().toUpperCase().regex(/^[A-Z0-9]{8,11}$/, 'BIC: 8 oder 11 Zeichen.').nullable(),
    account_holder: text(200),
  })
  .partial()
  .strict('Dieses Feld gehört nicht zum Profil.')
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Es wurde nichts geändert.' })
export type ProfilPatch = z.infer<typeof profilPatchSchema>
